import { Component } from '@theme/component';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Local storage key used by recently-viewed-products.js
   * @type {string}
   */
  static #RECENTLY_VIEWED_KEY = 'viewedProducts';

  /**
   * Swym localStorage key for wishlist products.
   * @type {string}
   */
  static #SWYM_PRODUCTS_KEY = 'swym-products';

  /**
   * Preserve original recommendations markup so the Recommended tab can restore instantly.
   * @type {string}
   */
  #recommendedMarkup = '';

  /**
   * Cache for recently viewed rendered markup.
   * @type {string}
   */
  #recentlyViewedMarkup = '';

  /**
   * In-flight promise for recently viewed markup build — prevents duplicate
   * API calls when the tab is clicked multiple times before the first resolves.
   * @type {Promise<string> | null}
   */
  #recentlyViewedFetchPromise = null;

  /**
   * Cache for wishlist rendered markup.
   * @type {string}
   */
  #wishlistMarkup = '';

  /**
   * Track wishlisted product ids for icon active state.
   * @type {Set<string>}
   */
  #wishlistProductIds = new Set();

  /** @type {boolean} */
  #swymEventsBound = false;

  /** @type {boolean} */
  #searchspringBisEventsBound = false;

  /** @type {Record<string, string>} */
  #wishlistSizeCache = {};
  /** @type {Record<string, { sizeLabel: string, variantId: string, available: boolean, hasAvailableVariant: boolean }>} */
  #wishlistVariantCache = {};

  /** @type {Record<string, Promise<any>>} */
  #wishlistProductJsonCache = {};

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#initCarouselProgress();
    this.#bindSwymWishlistEvents();
    this.#bindSearchspringBisEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    // If the recommendations have already been loaded (metafield / view-cart SearchSpring),
    // skip the fetch but still initialise tabs so they are interactive.
    if (recommendationsPerformed === 'true') {
      this.#initRecommendationTabs();
      return;
    }

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          this.#recommendedMarkup = '';
          this.#recentlyViewedMarkup = '';
          this.#wishlistMarkup = '';
          this.#initRecommendationTabs();
          this.#initCarouselProgress();
          document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }

  /**
   * Initialize progress widgets for recommendation carousels.
   */
  #initCarouselProgress() {
    /** @type {NodeListOf<HTMLElement>} */
    const widgets = this.querySelectorAll('[data-product-list-carousel-progress]');
    if (!widgets.length) return;

    const prefersReducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

    widgets.forEach((widget) => {
      if (widget.dataset.initialized === 'true') return;

      const { carouselRef } = widget.dataset;
      /** @type {any} */
      const slideshow = this.querySelector(`slideshow-component[ref="${carouselRef}"]`);
      /** @type {HTMLElement | null} */
      const track = widget.querySelector('.product-list__carousel-progress-track');
      /** @type {HTMLElement | null} */
      const thumb = widget.querySelector('[data-product-list-progress-thumb]');
      /** @type {HTMLButtonElement | null} */
      const prev = widget.querySelector('[data-product-list-arrow-prev]');
      /** @type {HTMLButtonElement | null} */
      const next = widget.querySelector('[data-product-list-arrow-next]');

      if (!slideshow || !track || !thumb) return;

      /** @type {HTMLElement | null} */
      let scrollerEl = slideshow.querySelector('slideshow-slides');

      const updateArrows = () => {
        // Drive arrow disabled state from actual scroll bounds.
        if (scrollerEl) {
          const maxScroll = Math.max(0, scrollerEl.scrollWidth - scrollerEl.clientWidth);
          const currentScroll = Math.max(0, scrollerEl.scrollLeft);
          const threshold = 2;

          if (prev) {
            prev.disabled = maxScroll <= threshold || currentScroll <= threshold;
          }

          if (next) {
            next.disabled = maxScroll <= threshold || currentScroll >= maxScroll - threshold;
          }

          return;
        }

        if (prev) {
          const atStartAttr = slideshow.getAttribute('data-at-start');
          prev.disabled =
            atStartAttr === 'true' ||
            (slideshow.atStart !== undefined && slideshow.atStart === true);
        }

        if (next) {
          const atEndAttr = slideshow.getAttribute('data-at-end');
          next.disabled =
            atEndAttr === 'true' ||
            (slideshow.atEnd !== undefined && slideshow.atEnd === true);
        }
      };

      const updateThumb = () => {
        if (!scrollerEl) return;

        const maxScroll = scrollerEl.scrollWidth - scrollerEl.clientWidth;
        const trackWidth = track.clientWidth;

        if (maxScroll <= 0 || trackWidth <= 0) {
          thumb.style.width = '100%';
          thumb.style.transform = 'translateX(0px)';
          return;
        }

        const progress = scrollerEl.scrollLeft / maxScroll;
        const thumbWidth = Math.min(
          trackWidth,
          Math.max(16, trackWidth * (scrollerEl.clientWidth / scrollerEl.scrollWidth))
        );
        const x = progress * (trackWidth - thumbWidth);

        thumb.style.width = `${thumbWidth}px`;
        thumb.style.transform = `translateX(${x}px)`;
      };

      const update = () => {
        updateThumb();
        updateArrows();
      };

      /** @type {(() => void) & { rafId?: number | null }} */
      const onScroll = () => {
        if (onScroll.rafId) return;
        onScroll.rafId = requestAnimationFrame(() => {
          onScroll.rafId = null;
          update();
        });
      };
      onScroll.rafId = null;

      /** @param {HTMLElement} el */
      const start = (el) => {
        scrollerEl = el;
        scrollerEl.addEventListener('scroll', onScroll, { passive: true });

        if (window.ResizeObserver) {
          const ro = new ResizeObserver(update);
          ro.observe(scrollerEl);
          ro.observe(track);
        }

        slideshow.addEventListener('slideshow:select', update);
        update();
      };

      if (scrollerEl) {
        start(scrollerEl);
      } else {
        const obs = new MutationObserver(() => {
          const maybe = slideshow.querySelector('slideshow-slides');
          if (!maybe) return;
          obs.disconnect();
          start(maybe);
        });
        obs.observe(slideshow, { childList: true, subtree: true });
      }

      /** @param {MouseEvent} event */
      const handleTrackClick = (event) => {
        if (!scrollerEl) return;
        const rect = track.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const ratio = rect.width > 0 ? clickX / rect.width : 0;

        const maxScroll = scrollerEl.scrollWidth - scrollerEl.clientWidth;
        if (maxScroll <= 0) return;

        scrollerEl.scrollTo({
          left: ratio * maxScroll,
          behavior: scrollBehavior,
        });
      };

      track.addEventListener('click', handleTrackClick);

      prev?.addEventListener('click', (event) => {
        if (typeof slideshow.previous === 'function') {
          slideshow.previous(event);
        } else {
          slideshow.dispatchEvent(new CustomEvent('previous', { bubbles: true }));
        }
      });

      next?.addEventListener('click', (event) => {
        if (typeof slideshow.next === 'function') {
          slideshow.next(event);
        } else {
          slideshow.dispatchEvent(new CustomEvent('next', { bubbles: true }));
        }
      });

      widget.dataset.initialized = 'true';
    });
  }

  /**
   * Initialize tabs for recommendation / recently viewed / wishlist views.
   */
  #initRecommendationTabs() {
    /** @type {HTMLElement | null} */
    const resultsContainer = this.querySelector('[data-product-recommendations-results]');
    if (!resultsContainer) return;

    /** @type {HTMLElement | null} */
    const searchspringContainer = this.querySelector(
      '[data-product-recommendations-searchspring]'
    );

    /** @type {HTMLElement | null} */
    const searchspringLoading = this.querySelector('[data-product-recommendations-loading]');

    /** @returns {boolean} */
    const hasSearchspringResults = () => {
      if (!(searchspringContainer instanceof HTMLElement)) return false;

      const results = searchspringContainer.querySelectorAll(
        '.ss__searchspring__recommendations .ss__result, .ss__searchspring__recommendations .swiper-slide'
      );

      return results.length > 0;
    };

    /** @param {boolean} isLoading */
    const setSearchspringLoading = (isLoading) => {
      if (!(searchspringLoading instanceof HTMLElement)) return;
      searchspringLoading.hidden = !isLoading;

      if (searchspringContainer instanceof HTMLElement) {
        searchspringContainer.style.visibility = isLoading ? 'hidden' : 'visible';
      }
    };

    const syncSearchspringLoadingState = () => {
      if (!(searchspringContainer instanceof HTMLElement)) return;

      if (hasSearchspringResults()) {
        searchspringContainer.dataset.searchspringLoaded = 'true';
        setSearchspringLoading(false);
      }
    };

    if (searchspringContainer instanceof HTMLElement) {
      if (searchspringContainer.dataset.searchspringLoaderBound !== 'true') {
        const loadingObserver = new MutationObserver(() => {
          syncSearchspringLoadingState();
        });

        loadingObserver.observe(searchspringContainer, {
          childList: true,
          subtree: true,
        });

        window.addEventListener('ss-on-results-load', () => {
          syncSearchspringLoadingState();
        });

        searchspringContainer.dataset.searchspringLoaderBound = 'true';
      }

      requestAnimationFrame(() => {
        syncSearchspringLoadingState();
      });
    }

    if (!this.#recommendedMarkup) {
      this.#recommendedMarkup = resultsContainer.innerHTML;
    }

    /** @type {HTMLButtonElement[]} */
    let tabs = Array.from(this.querySelectorAll('[data-product-recommendations-tab]'));
    if (!tabs.length) return;

    const recentlyViewedTab = tabs.find(
      (tab) => tab.dataset.productRecommendationsTab === 'recently-viewed'
    );
    if (recentlyViewedTab && this.#getRecentlyViewedProductsFromLocalStorage().length === 0) {
      if (tabs.length === 1) {
        this.classList.add('hidden');
        return;
      }

      recentlyViewedTab.hidden = true;
      tabs = tabs.filter((tab) => tab !== recentlyViewedTab);

      const tabsContainer = this.querySelector('.product-recommendations__tabs');
      if (tabsContainer instanceof HTMLElement && tabs.length < 2) {
        tabsContainer.hidden = true;
      }
    }

    /** @param {string} tabType */
    const setActiveTab = (tabType) => {
      tabs.forEach((tab) => {
        const isActive = tab.dataset.productRecommendationsTab === tabType;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      if (tabType === 'recommended') {
        const addSearchspringDisplayBlock = () => {
          document
            .querySelectorAll('div[searchspring-recommend]')
            .forEach((el) => {
              if (el instanceof HTMLElement) el.classList.add('display-block');
            });
        };

        addSearchspringDisplayBlock();

        // SearchSpring renders asynchronously — observe until div[searchspring-recommend] appears
        if (
          searchspringContainer instanceof HTMLElement &&
          !searchspringContainer.querySelector('div[searchspring-recommend]')
        ) {
          const ssObserver = new MutationObserver(() => {
            if (
              searchspringContainer.querySelector('div[searchspring-recommend]')
            ) {
              ssObserver.disconnect();
              addSearchspringDisplayBlock();
            }
          });
          ssObserver.observe(searchspringContainer, {
            childList: true,
            subtree: true,
          });
        }
      }

      if (searchspringContainer instanceof HTMLElement) {
        const showSearchspring = tabType === 'recommended';
        searchspringContainer.hidden = !showSearchspring;
        resultsContainer.hidden = showSearchspring;

        if (showSearchspring) {
          const loaded =
            searchspringContainer.dataset.searchspringLoaded === 'true' ||
            hasSearchspringResults();
          setSearchspringLoading(!loaded);
        } else {
          setSearchspringLoading(false);
        }

        // Slideshow measurements can be wrong after hidden->visible; re-init on next frame.
        if (!showSearchspring) {
          requestAnimationFrame(() => {
            this.#resetCarouselProgressWidgets(resultsContainer);
            this.#initCarouselProgress();
          });
        }
      }
    };

    const renderRecommended = () => {
      resultsContainer.innerHTML = this.#recommendedMarkup;
      this.#resetCarouselProgressWidgets(resultsContainer);
      this.#initCarouselProgress();
    };

    const renderRecentlyViewed = async () => {
      resultsContainer.replaceChildren();
      this.#showResultsLoading(resultsContainer);

      if (!this.#recentlyViewedMarkup) {
        if (!this.#recentlyViewedFetchPromise) {
          this.#recentlyViewedFetchPromise = this.#buildRecentlyViewedMarkup().finally(() => {
            this.#recentlyViewedFetchPromise = null;
          });
        }
        this.#recentlyViewedMarkup = await this.#recentlyViewedFetchPromise;
      }

      if (!this.#recentlyViewedMarkup) {
        resultsContainer.innerHTML =
          '<p class="product-recommendations__empty">No recently viewed products.</p>';
        this.#hideResultsLoading(resultsContainer);
        return;
      }

      resultsContainer.innerHTML = this.#recentlyViewedMarkup;
      this.#bindWishlistAddToCartHandlers(resultsContainer);
      this.#resetCarouselProgressWidgets(resultsContainer);
      this.#initCarouselProgress();
      this.#hideResultsLoading(resultsContainer);
      if(typeof Samita == "object" && typeof Samita.LockAccess == "object" && typeof Samita.LockAccess.start == "function"){
        Samita.LockAccess.start();
      }

    };

    const renderWishlist = async () => {
      resultsContainer.replaceChildren();
      this.#showResultsLoading(resultsContainer);

      if (!this.#wishlistMarkup) {
        this.#wishlistMarkup = await this.#buildWishlistMarkup();
      }

      if (!this.#wishlistMarkup) {
        resultsContainer.innerHTML =
          '<p class="product-recommendations__empty">No products in your wishlist.</p>';
        this.#hideResultsLoading(resultsContainer);
        return;
      }

      resultsContainer.innerHTML = this.#wishlistMarkup;
      this.#bindWishlistRemoveHandlers(resultsContainer);
      this.#bindWishlistAddToCartHandlers(resultsContainer);
      this.#resetCarouselProgressWidgets(resultsContainer);
      this.#initCarouselProgress();
      this.#hideResultsLoading(resultsContainer);
    };

    /**
     * Activate a tab and render its content, so an active tab can never be left
     * displaying another tab's markup.
     * @param {string} tabType
     * @param {{ initial?: boolean }} [options]
     */
    const activateTab = async (tabType, { initial = false } = {}) => {
      setActiveTab(tabType);

      if (tabType === 'wishlist') {
        await renderWishlist();
        return;
      }

      if (tabType === 'recently-viewed') {
        await renderRecentlyViewed();
        return;
      }

      // The server-rendered markup already is the recommended state, so
      // re-rendering it on the initial activation would only cause a flash.
      if (tabType === 'recommended' && !initial) {
        renderRecommended();
      }
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', async () => {
        const tabType = tab.dataset.productRecommendationsTab;
        if (!tabType) return;

        await activateTab(tabType);
      });
    });

    if (this.dataset.smartTabSelection === 'true') {
      this.#selectSmartInitialTab(tabs, activateTab);
    } else {
      const initialActiveTab =
        tabs.find((tab) => tab.classList.contains('is-active'))?.dataset
          .productRecommendationsTab ||
        tabs.find((tab) => tab.dataset.productRecommendationsTab === 'recommended')?.dataset
          .productRecommendationsTab ||
        tabs[0]?.dataset.productRecommendationsTab;

      if (initialActiveTab) {
        activateTab(initialActiveTab, { initial: true });
      }
    }
  }

  /**
   * Auto-select the initial tab based on user data:
   * wishlist (if items exist) → recently viewed (if items exist) → recommended.
   * @param {HTMLButtonElement[]} tabs
   * @param {(tabType: string, options?: { initial?: boolean }) => Promise<void>} activateTab
   */
  async #selectSmartInitialTab(tabs, activateTab) {
    const hasTab = (type) => tabs.some((t) => t.dataset.productRecommendationsTab === type);

    if (hasTab('wishlist')) {
      const localWishlist = this.#getWishlistProductsFromLocalStorage();
      if (localWishlist.length > 0) {
        await activateTab('wishlist', { initial: true });
        return;
      }

      const wishlistProducts = await this.#fetchWishlistProducts();
      if (wishlistProducts.length > 0) {
        await activateTab('wishlist', { initial: true });
        return;
      }
    }

    if (hasTab('recently-viewed')) {
      const viewedProducts = this.#getRecentlyViewedProductsFromLocalStorage();
      if (viewedProducts.length > 0) {
        await activateTab('recently-viewed', { initial: true });
        return;
      }
    }

    // Nothing personalised to show: prefer the recommended tab, which always has
    // content, and only fall back to the first tab if it has been hidden via
    // section settings.
    const fallbackType = hasTab('recommended')
      ? 'recommended'
      : tabs[0]?.dataset.productRecommendationsTab;

    if (fallbackType) {
      await activateTab(fallbackType, { initial: true });
    }
  }

  /**
   * Reset widget init flags after replacing recommendation markup.
   * @param {HTMLElement} root
   */
  #resetCarouselProgressWidgets(root) {
    root.querySelectorAll('[data-product-list-carousel-progress]').forEach((widget) => {
      if (widget instanceof HTMLElement) {
        delete widget.dataset.initialized;
      }
    });
  }

  /**
   * @returns {string}
   */
  #getStorefrontAccessToken() {
    if (typeof window === 'undefined') return '';
    const t = window.storefrontAccessToken;
    return typeof t === 'string' ? t.trim() : '';
  }

  /**
   * @param {string} gid
   * @returns {string}
   */
  #gidToNumericId(gid) {
    if (!gid || typeof gid !== 'string') return '';
    const parts = gid.split('/');
    return parts[parts.length - 1] || '';
  }

  /**
   * Map a Storefront `Product` node + viewed entry into the shape expected by `#buildRecentlyViewedCardItem`.
   * @param {any} productNode
   * @param {{ id: string, variantId?: string, variantTitle?: string }} entry
   * @returns {any | null}
   */
  #mapStorefrontProductToRvCardShape(productNode, entry) {
    if (!productNode || productNode.__typename !== 'Product') return null;

    const numericId = this.#gidToNumericId(productNode.id);
    const handle = productNode.handle || '';
    const variants = (productNode.variants?.edges || []).map(
      /** @param {{ node: any }} e */ (e) => e.node
    );
    const options = Array.isArray(productNode.options) ? productNode.options : [];

    /** @type {any | null} */
    let variant = null;
    if (entry.variantId) {
      variant =
        variants.find((v) => this.#gidToNumericId(v.id) === String(entry.variantId)) || null;
    }
    if (!variant) {
      variant = variants.find((v) => v.availableForSale) || variants[0] || null;
    }
    if (!variant) return null;

    let productUrl = productNode.onlineStoreUrl || '';
    if (!productUrl && handle) {
      productUrl = `/products/${handle}`;
    }
    if (!productUrl) return null;

    const imageUrl = variant.image?.url || productNode.featuredImage?.url || '';
    const altText =
      variant.image?.altText ||
      productNode.featuredImage?.altText ||
      productNode.title ||
      '';

    const priceAmount = variant.price?.amount ?? '';
    const compareAmount = variant.compareAtPrice?.amount ?? '';

    const storedTitle = (entry.variantTitle || '').trim();
    const variantTitle =
      storedTitle ||
      (variant.title && variant.title !== 'Default Title' ? variant.title : '');

    const variantNumericId = this.#gidToNumericId(variant.id);
    const normalizedVariants = variants
      .map((item) => ({
        id: this.#gidToNumericId(item.id),
        title: item.title || '',
        available: item.availableForSale !== false,
        selectedOptions: Array.isArray(item.selectedOptions) ? item.selectedOptions : [],
        price: item.price?.amount ?? '',
        compareAtPrice: item.compareAtPrice?.amount ?? '',
        imageUrl: item.image?.url || '',
        imageAlt: item.image?.altText || '',
      }))
      .filter((item) => item.id);

    return {
      id: numericId,
      handle,
      url: productUrl,
      title: productNode.title,
      featured_image: { url: imageUrl, alt: altText, image: imageUrl },
      image: imageUrl,
      price: priceAmount,
      compare_at_price: compareAmount,
      compare_at_price_min: compareAmount,
      __variantId: entry.variantId ? String(entry.variantId) : variantNumericId,
      __variantTitle: variantTitle,
      __available: variant.availableForSale !== false,
      __options: options,
      __variants: normalizedVariants,
    };
  }

  /**
   * Load recently viewed rows via Storefront GraphQL (`nodes`).
   * Returns an empty array when the token/domain is missing, the request fails, or no products resolve.
   * @param {Array<{ id: string, variantId?: string, variantTitle?: string }>} viewedEntries
   * @param {number} maxProducts
   * @returns {Promise<any[]>}
   */
  async #fetchRecentlyViewedOrderedProductsStorefront(viewedEntries, maxProducts) {
    const token = this.#getStorefrontAccessToken();
    const domain = (this.dataset.shopDomain || '').trim();
    const version = (this.dataset.storefrontApiVersion || '2024-10').trim();

    if (!token || !domain) return [];

    const slice = viewedEntries.slice(0, maxProducts);
    const uniqueGids = [];
    const seen = new Set();
    for (const e of slice) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      uniqueGids.push(`gid://shopify/Product/${e.id}`);
    }

    if (!uniqueGids.length) return [];

    const gql = `
      query RecentlyViewedProducts($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on Product {
            __typename
            id
            handle
            title
            onlineStoreUrl
            featuredImage {
              url(transform: { maxWidth: 800 })
              altText
            }
            options {
              name
              values
            }
            variants(first: 100) {
              edges {
                node {
                  id
                  title
                  selectedOptions {
                    name
                    value
                  }
                  availableForSale
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  image {
                    url(transform: { maxWidth: 800 })
                    altText
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(`https://${domain}/api/${version}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: gql, variables: { ids: uniqueGids } }),
      });

      const payload = await response.json();
      if (!response.ok || payload.errors?.length) {
        if (payload.errors?.length) {
          console.warn('Storefront recently viewed:', payload.errors[0]?.message || payload.errors);
        }
        return [];
      }

      const nodes = payload?.data?.nodes;
      if (!Array.isArray(nodes)) return [];

      /** @type {Map<string, any>} */
      const nodeByProductId = new Map();
      for (const node of nodes) {
        if (node && node.__typename === 'Product') {
          nodeByProductId.set(this.#gidToNumericId(node.id), node);
        }
      }

      return slice
        .map((entry) => {
          const productNode = nodeByProductId.get(entry.id);
          return this.#mapStorefrontProductToRvCardShape(productNode, entry);
        })
        .filter(Boolean);
    } catch (error) {
      console.warn('Storefront recently viewed fetch failed:', error);
      return [];
    }
  }

  /**
   * Build recently viewed markup via Storefront GraphQL (`nodes`).
   * Returns empty markup when the token/domain is missing, the request fails, or no products resolve.
   * @returns {Promise<string>}
   */
  async #buildRecentlyViewedMarkup() {
    const sectionMaxProducts = Number.parseInt(this.dataset.maxProducts || '4', 10) || 4;
    const maxProducts = Math.max(sectionMaxProducts, 8);

    /** @type {Array<{ id: string, variantId?: string, variantTitle?: string }>} */
    let viewedEntries = [];
    try {
      const raw = JSON.parse(
        localStorage.getItem(ProductRecommendations.#RECENTLY_VIEWED_KEY) || '[]'
      );
      if (Array.isArray(raw)) {
        viewedEntries = raw
          .map((item) => (typeof item === 'string' ? { id: item } : item))
          .filter((item) => item && typeof item.id === 'string' && item.id);
      }
    } catch {
      viewedEntries = [];
    }

    if (!viewedEntries.length) return '';

    const orderedProducts = await this.#fetchRecentlyViewedOrderedProductsStorefront(
      viewedEntries,
      maxProducts
    );

    if (!orderedProducts.length) return '';

    await this.#hydrateWishlistIds();

    const cardItems = orderedProducts
      .map((product) => this.#buildRecentlyViewedCardItem(product))
      .filter(Boolean);
    if (!cardItems.length) return '';

    return this.#buildTabMarkupFromCardItems(cardItems);
  }

  /**
   * Build wishlist recommendations markup using Swym wishlist data.
   * @returns {Promise<string>}
   */
  async #buildWishlistMarkup() {
    const maxProducts = Number.parseInt(this.dataset.maxProducts || '4', 10) || 4;
    const wishlistProducts = await this.#fetchWishlistProducts();
    if (!wishlistProducts.length) return '';

    this.#wishlistProductIds = new Set(
      wishlistProducts
        .map((product) => String(product.empi || product.product_id || ''))
        .filter(Boolean)
    );

    const wishlistProductsWithSize = await Promise.all(
      wishlistProducts.slice(0, maxProducts).map(async (product) => {
        const resolved = await this.#resolveWishlistProductDetails(product);
        return {
          ...product,
          __sizeLabel: resolved.sizeLabel,
          __variantId: resolved.variantId,
          __variantAvailable: resolved.available,
          __hasAvailableVariant: resolved.hasAvailableVariant,
        };
      })
    );

    const cardItems = wishlistProductsWithSize
      .map((product) => this.#buildWishlistCardItem(product))
      .filter(Boolean);
    if (!cardItems.length) return '';

    return this.#buildTabMarkupFromCardItems(cardItems, 'resource-list--wishlist');
  }

  /**
   * Fetch wishlist products from Swym.
   * @returns {Promise<Array<any>>}
   */
  #fetchWishlistProducts() {
    return new Promise((resolve) => {
      const localWishlistProducts = this.#getWishlistProductsFromLocalStorage();
      if (localWishlistProducts.length) {
        resolve(localWishlistProducts);
        return;
      }

      if (!window._swat || !window._swat.fetchWrtEventTypeET || !window._swat.EventTypes) {
        resolve([]);
        return;
      }

      window._swat.fetchWrtEventTypeET(
        (wishlistProducts) => {
          resolve(Array.isArray(wishlistProducts) ? wishlistProducts : []);
        },
        window._swat.EventTypes.addToWishList
      );
    });
  }

  /**
   * Read wishlist items from swym-products localStorage payload.
   * @returns {Array<any>}
   */
  #getWishlistProductsFromLocalStorage() {
    /** @type {unknown} */
    let parsed = null;

    try {
      parsed = JSON.parse(
        localStorage.getItem(ProductRecommendations.#SWYM_PRODUCTS_KEY) || 'null'
      );
    } catch {
      return [];
    }

    if (!parsed) return [];

    if (Array.isArray(parsed)) {
      return parsed;
    }

    if (typeof parsed === 'object') {
      const maybeObject = /** @type {Record<string, any>} */ (parsed);

      if (Array.isArray(maybeObject.all)) {
        return maybeObject.all;
      }

      if (maybeObject.all && typeof maybeObject.all === 'object') {
        const nestedAll = /** @type {Record<string, any>} */ (maybeObject.all);
        if (Array.isArray(nestedAll.all)) {
          return nestedAll.all;
        }
      }
    }

    return [];
  }

  /**
   * Read viewed product entries from the recently-viewed-products.js localStorage payload.
   * @returns {Array<any>}
   */
  #getRecentlyViewedProductsFromLocalStorage() {
    let parsed = [];

    try {
      parsed = JSON.parse(
        localStorage.getItem(ProductRecommendations.#RECENTLY_VIEWED_KEY) || '[]'
      );
    } catch {
      return [];
    }

    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  }

  /**
   * Prime local wishlist ids state from Swym results.
   * @returns {Promise<void>}
   */
  async #hydrateWishlistIds() {
    if (this.#wishlistProductIds.size > 0) return;
    const wishlistProducts = await this.#fetchWishlistProducts();
    this.#wishlistProductIds = new Set(
      wishlistProducts
        .map((product) => String(product.empi || product.product_id || ''))
        .filter(Boolean)
    );
  }

  /**
   * Inject card items into existing recommendation markup structure.
   * @param {string[]} cardItems
   * @param {string} [wrapperClass]
   * @returns {string}
   */
  #buildTabMarkupFromCardItems(cardItems, wrapperClass = '') {
    if (!cardItems.length) return '';

    const wrapperClasses = ['resource-list', 'resource-list--grid'];
    if (wrapperClass) wrapperClasses.push(wrapperClass);

    if (!this.#recommendedMarkup || !this.#recommendedMarkup.trim()) {
      return `<div class="${wrapperClasses.join(' ')}" data-has-recommendations="true">${cardItems.join('')}</div>`;
    }

    const template = document.createElement('div');
    template.innerHTML = this.#recommendedMarkup;

    const buildSlideMarkup = () =>
      cardItems
        .map((itemMarkup) => `<slideshow-slide ref="slides[]" class="resource-list__slide">${itemMarkup}</slideshow-slide>`)
        .join('');

    template.querySelectorAll('slideshow-slides').forEach((slidesContainer) => {
      if (slidesContainer instanceof HTMLElement) {
        slidesContainer.innerHTML = buildSlideMarkup();
      }
    });

    template.querySelectorAll('.resource-list.resource-list--grid').forEach((grid) => {
      if (grid instanceof HTMLElement) {
        grid.innerHTML = cardItems.join('');
      }
    });

    template.querySelectorAll('.resource-list').forEach((list) => {
      if (list instanceof HTMLElement) {
        list.dataset.hasRecommendations = 'true';
      }
    });

    const hasResults =
      template.querySelectorAll('slideshow-slide').length > 0 ||
      template.querySelectorAll('.resource-list__item').length > 0;

    if (!hasResults) {
      return `<div class="${wrapperClasses.join(' ')}" data-has-recommendations="true">${cardItems.join('')}</div>`;
    }

    if (wrapperClass) {
      template.querySelectorAll('.resource-list').forEach((list) => {
        if (list instanceof HTMLElement) {
          list.classList.add(wrapperClass);
        }
      });
    }

    return template.innerHTML;
  }

  /**
   * Build wishlist product card markup from Swym payload.
   * @param {any} product
   * @returns {string}
   */
  #buildWishlistCardItem(product) {
    if (!product || !product.du) return '';

    const title = this.#escapeHtml(product.dt || 'Product');
    const url = this.#escapeHtml(product.du);
    const imageSource = product.iu || '';
    const imageAlt = this.#escapeHtml(product.dt || '');
    const variantId = this.#escapeHtml(product.__variantId || product.epi || '');
    const productId = this.#escapeHtml(product.empi || '');
    const hasAvailableVariant = product.__hasAvailableVariant === true;
    const variantAvailable = product.__variantAvailable === true && hasAvailableVariant;

    const price = this.#formatRecentlyViewedPrice(product.pr);
    const compareAtPrice = this.#formatRecentlyViewedPrice(product.op);
    const hasCompareAtPrice =
      Number.parseFloat(product.op || '0') > Number.parseFloat(product.pr || '0');
    const sizeLabel = this.#escapeHtml(product.__sizeLabel || '-');
    const ctaLabel = variantAvailable ? 'Add to cart' : 'Sold out';

    if (!imageSource || !price) {
      return '';
    }

    return `
      <div class="resource-list__item" data-wishlist-key="${this.#escapeHtml(product.epi || product.empi || '')}">
        <article class="rv-product-card" style="position: relative;">
          <button
            type="button"
            class="rv-product-card__wishlist-btn swym-button swym-add-to-wishlist-view-product swym-loaded swym-added"
            data-wishlist-remove="true"
            data-wishlist-key="${this.#escapeHtml(product.epi || product.empi || '')}"
            data-lid="${this.#escapeHtml(product.lid || product.li || product.di || '')}"
            data-epi="${this.#escapeHtml(product.epi || '')}"
            data-empi="${this.#escapeHtml(product.empi || '')}"
            data-product-id="${this.#escapeHtml(product.empi || '')}"
            data-variant-id="${this.#escapeHtml(product.epi || '')}"
            data-product-url="${url}"
            data-du="${url}"
            aria-label="Remove from wishlist"
            title="Remove from wishlist"
            style="position:absolute;top:8px;right:8px;z-index:3;width:32px;height:32px;border:1px solid #111;border-radius:999px;background:#fff;color:#111;display:flex;align-items:center;justify-content:center;"
          >
          </button>
          <a class="rv-product-card__link" href="${url}">
            <div class="rv-product-card__media">
              <img
                class="rv-product-card__image"
                src="${imageSource}"
                alt="${imageAlt}"
                loading="lazy"
              />
            </div>
            <div class="rv-product-card__content">
              <p class="rv-product-card__title">${title}</p>
              <p class="rv-product-card__price">
                ${hasCompareAtPrice ? `<s class="rv-product-card__compare">${compareAtPrice}</s>` : ''}
                <span>${price}</span>
              </p>
              <p class="rv-product-card__size">Size: ${sizeLabel}</p>
            </div>
          </a>
          <button
            type="button"
            class="button add-to-cart-button rv-product-card__add-to-cart"
            data-wishlist-add-to-cart="true"
            data-product-id="${productId}"
            data-variant-id="${variantId}"
            data-product-url="${url}"
            ${variantAvailable ? '' : 'disabled aria-disabled="true"'}
          >
            ${ctaLabel}
          </button>
        </article>
      </div>
    `;
  }

  /**
   * Resolve wishlist product variant details.
   * @param {any} product
   * @returns {Promise<{ sizeLabel: string, variantId: string, available: boolean, hasAvailableVariant: boolean }>}
   */
  async #resolveWishlistProductDetails(product) {
    const directSizeFields = [
      product?.size,
      product?.size_name,
      product?.variant_size,
      product?.variant_title,
      product?.variantTitle,
      product?.vt,
      product?.vi,
    ];

    const direct =
      directSizeFields.find((value) => typeof value === 'string' && value.trim()) || '';

    // Swym stores epi as float string (e.g. "42178294104117.0"); normalise to integer string.
    const rawEpi = String(product?.epi || '');
    const variantId =
      rawEpi && !Number.isNaN(Number(rawEpi)) ? String(Math.round(Number(rawEpi))) : rawEpi;
    const cacheKey = variantId || String(product?.du || '');

    if (cacheKey && this.#wishlistVariantCache[cacheKey]) {
      return this.#wishlistVariantCache[cacheKey];
    }

    const swymAvailability = () => {
      const availability = String(product?.availability || '').toLowerCase();
      return (
        product?.av === true ||
        product?.available === true ||
        product?.availableForSale === true ||
        product?.inStock === true ||
        availability === 'in_stock' ||
        availability === 'available'
      );
    };

    const variantIsAvailable = (variant) =>
      variant?.available === true || variant?.availableForSale === true;

    const hasAvailableVariant = (variants) =>
      Array.isArray(variants) && variants.some((variant) => variantIsAvailable(variant));

    if (!product?.du) {
      const fallbackAvailable = swymAvailability();
      const fallback = {
        sizeLabel: direct,
        variantId,
        available: fallbackAvailable,
        hasAvailableVariant: fallbackAvailable,
      };
      if (cacheKey) this.#wishlistVariantCache[cacheKey] = fallback;
      return fallback;
    }

    let productJsonUrl = '';
    try {
      const productUrl = new URL(product.du, window.location.origin);
      // Always use current page's origin to avoid cross-origin issues when Swym stores
      // product URLs with a different domain (e.g. custom domain vs .myshopify.com).
      productJsonUrl = `${window.location.origin}${productUrl.pathname.replace(/\/$/, '')}.js`;

      // Cache the Promise so concurrent requests for the same product share one fetch.
      if (!this.#wishlistProductJsonCache[productJsonUrl]) {
        this.#wishlistProductJsonCache[productJsonUrl] = fetch(productJsonUrl).then(
          (res) => (res.ok ? res.json() : Promise.resolve({ _status: res.status })),
        );
      }
      const productJson = await this.#wishlistProductJsonCache[productJsonUrl];
      if (productJson?._status) {
        const fallbackAvailable = swymAvailability();
        const fallback = {
          sizeLabel: direct,
          variantId,
          available: fallbackAvailable,
          hasAvailableVariant: fallbackAvailable,
        };
        if (cacheKey) this.#wishlistVariantCache[cacheKey] = fallback;
        return fallback;
      }
      const variants = Array.isArray(productJson?.variants) ? productJson.variants : [];
      const productHasAvailableVariant = hasAvailableVariant(variants);
      let variant = variants.find((/** @type {any} */ item) => String(item.id) === variantId);
      if ((!variant || !variantIsAvailable(variant)) && productHasAvailableVariant) {
        variant = variants.find((/** @type {any} */ item) => variantIsAvailable(item)) || variant;
      }
      if (!variant) {
        const fallbackAvailable = variants.length
          ? productHasAvailableVariant
          : swymAvailability();
        const fallback = {
          sizeLabel: direct,
          variantId,
          available: fallbackAvailable,
          hasAvailableVariant: fallbackAvailable,
        };
        if (cacheKey) this.#wishlistVariantCache[cacheKey] = fallback;
        return fallback;
      }

      const optionNames = Array.isArray(productJson?.options) ? productJson.options : [];
      const sizeOptionIndex = optionNames.findIndex(
        (/** @type {any} */ name) => typeof name === 'string' && name.toLowerCase().includes('size'),
      );

      const byOptionIndex =
        sizeOptionIndex === 0
          ? variant.option1
          : sizeOptionIndex === 1
            ? variant.option2
            : sizeOptionIndex === 2
              ? variant.option3
              : '';

      const resolved = {
        sizeLabel:
          byOptionIndex ||
          direct ||
          (variant.title && variant.title !== 'Default Title' ? variant.title : '') ||
          '',
        variantId: String(variant.id || variantId),
        available: variantIsAvailable(variant),
        hasAvailableVariant: productHasAvailableVariant,
      };
      if (cacheKey) this.#wishlistVariantCache[cacheKey] = resolved;
      return resolved;
    } catch {
      const fallbackAvailable = swymAvailability();
      const fallback = {
        sizeLabel: direct,
        variantId,
        available: fallbackAvailable,
        hasAvailableVariant: fallbackAvailable,
      };
      if (cacheKey) this.#wishlistVariantCache[cacheKey] = fallback;
      return fallback;
    }
  }

  /**
   * Resolve wishlist product size label.
   * @param {any} product
   * @returns {Promise<string>}
   */
  async #resolveWishlistProductSize(product) {
    const resolved = await this.#resolveWishlistProductDetails(product);
    return resolved.sizeLabel;
  }

  /**
   * Bind add-to-cart behavior for wishlist tab cards.
   * @param {HTMLElement} root
   */
  #bindWishlistAddToCartHandlers(root) {
    const buttons = root.querySelectorAll('[data-wishlist-add-to-cart="true"]');
    if (!buttons.length) return;

    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';

      button.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const { variantId, productId } = button.dataset;
        if (!variantId || button.disabled) return;

        button.disabled = true;

        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity: 1 }],
            }),
          });

          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body?.description || response.statusText);
          }

          const cartItem = await response.json();
          dispatchCartUpdate(undefined, {
            product_id: productId,
            variant_id: variantId,
            quantity: 1,
            ...cartItem,
          });
        } catch (error) {
          alert(error instanceof Error ? error.message : 'Failed to add item to cart. Please try again.');
          console.error(error);
        } finally {
          button.disabled = false;
        }
      });
    });
  }

  /**
   * Build a simple product card markup for Recently Viewed products.
   * When __variantId / __variantTitle are present (new format) the card renders
   * a size label and an Add to cart / Sold out button.
   * Old-format entries (no variant data) render as a plain link only.
   * @param {any} product
   * @returns {string}
   */
  #buildRecentlyViewedCardItem(product) {
    if (!product || !product.url) return '';

    const productId = this.#escapeHtml(String(product.id || ''));
    const title = this.#escapeHtml(product.title || 'Product');
    const cleanUrl = this.#stripTrackingParams(product.url);
    const url = this.#escapeHtml(cleanUrl);
    const absoluteProductUrl = this.#escapeHtml(
      new URL(cleanUrl, window.location.origin).toString()
    );

    const imageSource =
      product.featured_image?.url || product.featured_image?.image || product.image || '';
    const imageAlt = this.#escapeHtml(product.featured_image?.alt || product.title || '');

    const price = this.#formatRecentlyViewedPrice(product.price);
    const compareAtPrice = this.#formatRecentlyViewedPrice(
      product.compare_at_price || product.compare_at_price_min
    );
    const hasCompareAtPrice =
      Number.parseFloat(product.compare_at_price || product.compare_at_price_min || '0') >
      Number.parseFloat(product.price || '0');

    if (!imageSource || !price) return '';

    const isWishlisted = this.#wishlistProductIds.has(String(product.id || ''));

    // New format: render the shared size picker when the Storefront payload
    // contains enough variant data; otherwise fall back to the single variant button.
    const variantId = this.#escapeHtml(String(product.__variantId || ''));
    const hasVariantId = Boolean(product.__variantId);
    const available = product.__available !== false;
    const ctaLabel = available ? 'Add to cart' : 'Sold out';
    const sizePickerMarkup = this.#buildRecentlyViewedSizePickerMarkup(product);

    return `
      <div class="resource-list__item">
        <article class="rv-product-card product-card" data-product-id="${productId}" style="position: relative; display: block; height: 100%;">
          <button
            id="swym-collections"
            type="button"
            class="rv-product-card__wishlist-btn swym-button swym-add-to-wishlist-view-product swym-loaded ${isWishlisted ? 'swym-added' : ''}"
            data-recently-viewed-wishlist-trigger="true"
            data-product-id="${productId}"
            data-variant-id="${variantId}"
            data-product-url="${absoluteProductUrl}"
            aria-label="Add to wishlist"
            title="Add to wishlist"
            style="position:absolute;top:8px;right:8px;z-index:3;width:32px;height:32px;border:1px solid #111;border-radius:999px;background:#fff;color:#111;display:flex;align-items:center;justify-content:center;"
          >
          </button>
          <a class="rv-product-card__link" href="${url}">
            <div class="rv-product-card__media">
              <img
                class="rv-product-card__image"
                src="${imageSource}"
                alt="${imageAlt}"
                loading="lazy"
              />
            </div>
          </a>
            <div class="rv-product-card__content">
              <a class="rv-product-card__title-link" href="${url}">
                <p class="rv-product-card__title">${title}</p>
              </a>
              <div class="product-card__price-row">
                <p class="rv-product-card__price">
                  ${hasCompareAtPrice ? `<s class="rv-product-card__compare">${compareAtPrice}</s>` : ''}
                  <span>${price}</span>
                </p>
                ${sizePickerMarkup}
              </div>
            </div>
          ${
            hasVariantId && !sizePickerMarkup
              ? `<button
            type="button"
            class="button add-to-cart-button rv-product-card__add-to-cart"
            data-wishlist-add-to-cart="true"
            data-product-id="${productId}"
            data-variant-id="${variantId}"
            data-product-url="${absoluteProductUrl}"
            ${available ? '' : 'disabled aria-disabled="true"'}
          >${ctaLabel}</button>`
              : ''
          }
        </article>
      </div>
    `;
  }

  /**
   * Build the same size picker controls used by snippets/product-card-size-picker.liquid.
   * @param {any} product
   * @returns {string}
   */
  #buildRecentlyViewedSizePickerMarkup(product) {
    const sizeVariants = this.#getRecentlyViewedSizeVariants(product);
    if (!sizeVariants.length) return '';

    const handle = this.#escapeHtml(
      product.handle || this.#getProductHandleFromUrl(product.url) || ''
    );
    const title = this.#escapeHtml(product.title || 'Product');
    const dialogId = this.#escapeHtml(
      `product-card-size-dialog-${product.id || 'product'}-${this.id || 'recently-viewed'}`
        .replaceAll('/', '-')
        .replaceAll(':', '-')
    );
    const dialogPrice = this.#escapeHtml(this.#formatRecentlyViewedDialogPrice(product));

    const desktopButtons = sizeVariants
      .map((variant) => {
        const variantId = this.#escapeHtml(variant.id);
        const label = this.#escapeHtml(variant.label);
        const available = variant.available === true;
        return `
          <button
            type="button"
            class="product-card__size-btn${available ? '' : ' product-card__size-btn--unavailable'}"
            data-variant-id="${variantId}"
            data-variant-available="${available ? 'true' : 'false'}"
            data-size-label="${label}"
          >
            ${label}
          </button>
        `;
      })
      .join('');

    const dialogButtons = sizeVariants
      .map((variant) => {
        const variantId = this.#escapeHtml(variant.id);
        const label = this.#escapeHtml(variant.label);
        const available = variant.available === true;
        return `
          <button
            type="button"
            class="product-card-size-dialog__size${available ? '' : ' is-unavailable'}"
            data-variant-id="${variantId}"
            data-variant-available="${available ? 'true' : 'false'}"
            ${available ? '' : 'aria-disabled="true"'}
          >
            ${label}
          </button>
        `;
      })
      .join('');

    return `
      <div
        class="product-card__sizes-desktop hidden--mobile"
        data-size-picker-desktop
        data-product-handle="${handle}"
        aria-hidden="true"
      >
        ${desktopButtons}
      </div>

      <button
        type="button"
        class="product-card__size-open-btn hidden--desktop"
        aria-haspopup="dialog"
        aria-controls="${dialogId}"
        data-open-size-dialog
      >
        <span class="visually-hidden">Select size</span>
        <span class="product-card__size-open-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.2556 15.7552C9.71572 15.7449 10.0963 15.741 10.4765 15.726C10.7116 15.7168 10.946 15.7021 11.181 15.7245C11.2332 15.7295 11.2863 15.7306 11.3387 15.7288C11.7934 15.7122 12.2472 15.687 12.6984 15.6241C13.0232 15.5792 13.3494 15.5457 13.6781 15.5431C13.8841 15.5414 14.084 15.5039 14.2807 15.4461C14.396 15.4122 14.4961 15.3546 14.5767 15.2657C14.6447 15.1907 14.7144 15.117 14.7814 15.0412C14.8704 14.9407 14.9368 14.8268 14.959 14.6943C14.9842 14.5441 15.0131 14.3931 15.0199 14.2416C15.0384 13.8304 15.052 13.4189 15.0567 13.0075C15.0641 12.3581 15.0639 11.7088 15.0646 11.0595C15.0653 10.4985 15.0637 9.93738 15.0611 9.37652C15.0604 9.22639 15.075 9.07877 15.1037 8.93126C15.1232 8.82996 15.1312 8.72602 15.1387 8.62299C15.1445 8.5441 15.1385 8.46428 15.143 8.38527C15.1878 7.60911 15.2339 6.83317 15.2788 6.05712C15.2832 5.98108 15.2877 5.90459 15.2841 5.82866C15.2759 5.66081 15.2588 5.49329 15.1942 5.33584C15.0813 5.06073 14.896 4.87698 14.5748 4.86257C14.3461 4.85228 14.118 4.83067 13.8894 4.82107C13.5679 4.80723 13.2462 4.80071 12.9246 4.78951C12.3772 4.77041 11.8302 4.74972 11.2821 4.75886C11.001 4.76355 10.7192 4.76469 10.441 4.70546C10.3145 4.67848 10.1847 4.66293 10.0555 4.65195C9.65479 4.61822 9.25397 4.58129 8.85118 4.62005C8.8144 4.62348 8.77691 4.62085 8.7399 4.62096C8.13031 4.62337 7.52073 4.62462 6.91114 4.6284C6.69472 4.62977 6.47737 4.62622 6.2621 4.64429C6.04266 4.6627 5.82427 4.67814 5.60436 4.6683C5.07995 4.64475 4.5588 4.67459 4.04228 4.76767C3.95443 4.78356 3.86752 4.8078 3.78315 4.83662C3.68904 4.86863 3.62916 4.93815 3.6085 5.03466C3.59585 5.09389 3.58738 5.15495 3.58541 5.21555C3.58205 5.31297 3.58599 5.41062 3.58634 5.50816C3.58785 5.85862 3.61581 6.20783 3.60966 6.5592C3.59725 7.26013 3.60676 7.96152 3.60827 8.66267C3.60885 8.87901 3.60073 9.09592 3.6432 9.31009C3.66664 9.42797 3.66699 9.54666 3.6504 9.66524C3.62835 9.82337 3.6302 9.98163 3.6461 10.1398C3.662 10.2981 3.66362 10.4559 3.64355 10.6143C3.62231 10.7813 3.62777 10.9484 3.64471 11.1161C3.6584 11.2523 3.67221 11.39 3.66618 11.5263C3.65713 11.7338 3.65423 11.9404 3.67465 12.1473C3.68416 12.2444 3.68683 12.342 3.69287 12.4394C3.70923 12.6981 3.7184 12.9572 3.74393 13.2149C3.78536 13.6335 3.83816 14.0511 3.88515 14.469C3.91556 14.7403 4.03474 14.9828 4.13558 15.2314C4.13906 15.2397 4.14452 15.2475 4.14939 15.2552C4.20242 15.3393 4.27541 15.3988 4.3751 15.4176C4.44762 15.4314 4.52201 15.4365 4.5957 15.4446C4.71534 15.4578 4.83556 15.4681 4.95486 15.4836C5.17882 15.5125 5.40244 15.5432 5.62595 15.5751C5.80024 15.5999 5.97512 15.6228 6.14815 15.6547C6.35854 15.6934 6.56869 15.7257 6.78373 15.7211C6.96615 15.7171 7.1488 15.7125 7.33111 15.7172C7.99906 15.734 8.66679 15.7612 9.2556 15.7552ZM17.3853 5.09417C17.3727 5.39867 17.361 5.70328 17.3471 6.00789C17.342 6.1204 17.34 6.23406 17.3223 6.34498C17.2829 6.59139 17.2975 6.8378 17.3114 7.08421C17.3486 7.74809 17.3648 8.41231 17.3522 9.07711C17.3427 9.58056 17.2913 10.081 17.244 10.582C17.2259 10.7732 17.2083 10.9646 17.2002 11.1563C17.1867 11.4704 17.1598 11.7834 17.1093 12.0935C17.0475 12.4738 17.0245 12.8566 17.0087 13.2407C16.9942 13.594 16.9791 13.9471 16.9594 14.3003C16.952 14.4341 16.9271 14.5667 16.9168 14.7004C16.8972 14.9524 16.8783 15.2048 16.8669 15.4573C16.8592 15.6313 16.844 15.8034 16.8091 15.9745C16.7762 16.1357 16.7481 16.298 16.7264 16.4611C16.7095 16.5864 16.6551 16.6839 16.5569 16.7645C16.4669 16.8384 16.3869 16.9243 16.2967 16.9977C16.2331 17.0494 16.1645 17.1012 16.0894 17.1322C15.9009 17.2093 15.709 17.2785 15.5158 17.3435C15.2671 17.4269 15.0132 17.4413 14.7572 17.3746C14.5761 17.3272 14.3927 17.3108 14.2054 17.3251C14.11 17.3323 14.0137 17.3284 13.9177 17.3285C13.0855 17.3299 12.2529 17.3246 11.4208 17.3346C10.5051 17.3453 9.5896 17.3786 8.67389 17.3858C8.18546 17.3896 7.69679 17.3588 7.20824 17.3432C7.00412 17.3366 6.8 17.3286 6.59599 17.3215C6.10083 17.3046 5.60787 17.2656 5.11804 17.1892C4.67092 17.1196 4.22043 17.0878 3.76809 17.0894C3.53936 17.0903 3.31087 17.1045 3.08215 17.1104C2.98641 17.1128 2.89032 17.1143 2.79494 17.1076C2.66613 17.0984 2.5488 17.0533 2.44053 16.9827C2.3347 16.914 2.22643 16.8487 2.11851 16.783C2.0367 16.7332 1.96487 16.6696 1.94781 16.5755C1.89187 16.2674 1.79997 15.9624 1.82584 15.6451C1.84116 15.4571 1.87853 15.271 1.90267 15.0835C1.94688 14.7412 1.96417 14.3991 1.94212 14.0529C1.89884 13.3744 1.88526 12.6945 1.90417 12.0144C1.91091 11.7738 1.90464 11.5326 1.90766 11.2919C1.91532 10.6669 1.92785 10.0422 1.93237 9.41716C1.93458 9.1185 1.91856 8.81983 1.9188 8.52117C1.91926 7.93893 1.92564 7.35658 1.92912 6.77434C1.92947 6.71339 1.93261 6.65176 1.92483 6.59162C1.88317 6.26665 1.89872 5.9402 1.89489 5.61421C1.89106 5.27746 1.86286 4.94346 1.74659 4.62353C1.65677 4.37655 1.63866 4.11779 1.6006 3.86154C1.56544 3.62565 1.59236 3.39331 1.62335 3.1605C1.64145 3.02421 1.70516 2.91284 1.81494 2.82708C1.85857 2.793 1.90151 2.7579 1.94282 2.72119C2.14729 2.53996 2.39226 2.48039 2.65777 2.50543C2.8171 2.52041 2.97747 2.54716 3.13147 2.58924C3.42912 2.67077 3.72968 2.7077 4.03836 2.71285C4.51774 2.72074 4.99689 2.74292 5.47604 2.76133C5.7578 2.77219 6.03816 2.77242 6.31945 2.74235C6.47855 2.72531 6.64078 2.73434 6.80162 2.73537C7.35539 2.73915 7.90915 2.74235 8.46292 2.75127C8.60148 2.75355 8.73946 2.78283 8.87813 2.789C9.12217 2.79998 9.36645 2.80615 9.61072 2.80821C10.9039 2.8185 12.1972 2.82868 13.4906 2.83451C13.7232 2.83554 13.9536 2.84034 14.1835 2.88139C14.2823 2.899 14.387 2.89534 14.4882 2.88871C14.9026 2.86149 15.3169 2.84343 15.7297 2.90106C15.9593 2.93296 16.187 2.97835 16.4158 3.01552C16.4951 3.02844 16.5756 3.0337 16.6551 3.0449C16.8026 3.06571 16.9366 3.11865 17.0454 3.22122C17.1189 3.29063 17.1916 3.36198 17.2572 3.4387C17.3341 3.52892 17.3755 3.63777 17.3986 3.75326C17.4293 3.9066 17.4387 4.06096 17.4301 4.21727C17.4137 4.50942 17.4016 4.80191 17.3878 5.09428C17.3869 5.09417 17.3862 5.09417 17.3853 5.09417Z" fill="black"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M6.61016 10.8565C6.21454 10.8431 5.8188 10.8303 5.42318 10.8157C5.36329 10.8135 5.30268 10.8075 5.24433 10.7954C5.07248 10.7593 4.95626 10.6598 4.90278 10.4987C4.88174 10.4355 4.86321 10.3698 4.85738 10.304C4.84027 10.11 4.84372 9.91592 4.87271 9.72287C4.89315 9.58616 4.96196 9.47893 5.07652 9.39607C5.17255 9.32674 5.26774 9.25594 5.36079 9.18298C5.47583 9.09283 5.60869 9.04638 5.75451 9.04274C5.98875 9.03716 6.22334 9.03432 6.45745 9.03967C6.94197 9.0506 7.42636 9.06699 7.91064 9.08292C7.99287 9.08554 8.07464 9.09897 8.15675 9.10296C8.22604 9.10637 8.29699 9.11127 8.36496 9.10148C8.54168 9.07575 8.64959 8.94599 8.64115 8.77581C8.63794 8.7123 8.63045 8.64855 8.61845 8.58606C8.5746 8.35454 8.56378 8.12085 8.58482 7.88773C8.6194 7.50276 8.63034 7.11881 8.5866 6.73373C8.57353 6.61968 8.58173 6.50335 8.57995 6.38815C8.57519 6.06943 8.79564 5.8782 9.0786 5.80376C9.12697 5.79101 9.17855 5.78691 9.22905 5.78452C9.39983 5.77644 9.5706 5.77075 9.7415 5.76437C9.76681 5.76335 9.79212 5.76301 9.81743 5.76301C10.0218 5.76357 10.1851 5.84166 10.306 6.00011C10.4512 6.19054 10.5504 6.39987 10.56 6.63732C10.5756 7.02832 10.5849 7.41966 10.5906 7.81101C10.5957 8.16592 10.5912 8.52095 10.5939 8.87598C10.5944 8.94212 10.6034 9.00939 10.619 9.07393C10.6471 9.18981 10.7248 9.26835 10.8459 9.29578C10.9221 9.31297 11.0031 9.32606 11.0805 9.3206C11.5764 9.28577 12.0751 9.31126 12.57 9.25105C12.778 9.22566 12.9885 9.23488 13.1938 9.28064C13.2887 9.30182 13.384 9.33016 13.4711 9.37091C13.606 9.43397 13.6777 9.547 13.6932 9.68951C13.701 9.76191 13.7022 9.83499 13.7032 9.90772C13.7046 10.0383 13.7034 10.1686 13.7038 10.2992C13.7042 10.4305 13.6585 10.5448 13.5551 10.6314C13.4913 10.6851 13.4223 10.7358 13.3481 10.7745C13.1083 10.8995 12.8542 10.9763 12.5768 10.9724C12.0764 10.9651 11.5758 10.9603 11.0754 10.9565C10.9839 10.9559 10.8916 10.9605 10.8007 10.9703C10.7481 10.9759 10.6951 10.9916 10.646 11.0115C10.5378 11.0551 10.4769 11.1364 10.4633 11.2479C10.4589 11.2839 10.4585 11.321 10.4623 11.357C10.4852 11.5762 10.4658 11.7939 10.4292 12.0099C10.3759 12.3245 10.3757 12.638 10.4336 12.9518C10.4977 13.298 10.4587 13.6415 10.4001 13.984C10.3924 14.0285 10.3784 14.0729 10.3612 14.1152C10.3022 14.2615 10.2781 14.4141 10.2746 14.5696C10.2716 14.7117 10.1982 14.8044 10.0641 14.8536C9.99653 14.8784 9.92392 14.8906 9.85475 14.9122C9.77679 14.9367 9.69622 14.9576 9.62384 14.9935C9.36204 15.1231 9.10712 15.1164 8.85435 14.9734C8.808 14.9472 8.75928 14.9248 8.71281 14.899C8.48761 14.7733 8.36413 14.5869 8.35058 14.3366C8.34773 14.2852 8.3469 14.2331 8.35225 14.1821C8.39277 13.7927 8.42664 13.4024 8.47905 13.0144C8.54085 12.5558 8.56283 12.0958 8.56711 11.6344C8.56783 11.5524 8.57282 11.4706 8.57163 11.3886C8.57068 11.3222 8.56878 11.2549 8.55772 11.1893C8.53205 11.0363 8.45932 10.9685 8.29865 10.94C8.25836 10.933 8.21713 10.9281 8.17613 10.926C7.88829 10.912 7.60058 10.898 7.31275 10.8862C7.07851 10.8765 6.84428 10.8697 6.61004 10.8617C6.61004 10.8599 6.61016 10.8582 6.61016 10.8565Z" fill="black"/>
        </svg>
      </span>
      </button>

      <dialog
        id="${dialogId}"
        class="product-card-size-dialog"
        data-product-card-size-dialog
        data-product-handle="${handle}"
      >
        <button
          type="button"
          class="product-card-size-dialog__close"
          data-close-size-dialog
          aria-label="Close"
        >
          &times;
        </button>
        <div class="product-card-size-dialog__header">
          <h3 class="product-card-size-dialog__title">${title}</h3>
          <p class="product-card-size-dialog__price">${dialogPrice}</p>
        </div>
        <div class="product-card-size-dialog__sizes" data-dialog-sizes>
          ${dialogButtons}
        </div>
        <button
          type="button"
          class="button product-card-size-dialog__add"
          data-dialog-add-to-cart
          hidden
        >
          Add to cart
        </button>
        <button
          type="button"
          class="button product-card-size-dialog__add"
          data-dialog-notify-me
          hidden
        >
          Notify me
        </button>
      </dialog>
    `;
  }

  /**
   * @param {any} product
   * @returns {Array<{ id: string, label: string, available: boolean }>}
   */
  #getRecentlyViewedSizeVariants(product) {
    const variants = Array.isArray(product.__variants) ? product.__variants : [];
    if (!variants.length) return [];

    const sizeOption = this.#getRecentlyViewedSizeOption(product);
    const sizeOptionName = sizeOption?.name || '';

    if (sizeOptionName && Array.isArray(sizeOption?.values) && sizeOption.values.length) {
      return sizeOption.values
        .map((value) => {
          const label = String(value || '').trim();
          if (!label) return null;

          const match = variants.find((variant) => {
            const selected = this.#getVariantSelectedOptionValue(variant, sizeOptionName);
            return selected.toLowerCase() === label.toLowerCase();
          });
          if (!match) return null;

          return {
            id: String(match.id || ''),
            label,
            available: match.available === true,
          };
        })
        .filter(Boolean);
    }

    const seen = new Set();
    return variants
      .map((variant) => {
        const label = this.#getVariantSizeLabel(variant, sizeOptionName);
        if (!label || seen.has(label.toLowerCase())) return null;
        seen.add(label.toLowerCase());
        return {
          id: String(variant.id || ''),
          label,
          available: variant.available === true,
        };
      })
      .filter(Boolean);
  }

  /**
   * @param {any} product
   * @returns {{ name: string, values: string[] } | null}
   */
  #getRecentlyViewedSizeOption(product) {
    const options = Array.isArray(product.__options) ? product.__options : [];
    return (
      options.find((option) => String(option?.name || '').toLowerCase() === 'size') ||
      options.find((option) => String(option?.name || '').toLowerCase().includes('size')) ||
      null
    );
  }

  /**
   * @param {any} variant
   * @param {string} optionName
   * @returns {string}
   */
  #getVariantSelectedOptionValue(variant, optionName) {
    const selectedOptions = Array.isArray(variant?.selectedOptions)
      ? variant.selectedOptions
      : [];
    const match = selectedOptions.find(
      (option) =>
        String(option?.name || '').toLowerCase() === String(optionName || '').toLowerCase()
    );
    return String(match?.value || '').trim();
  }

  /**
   * @param {any} variant
   * @param {string} sizeOptionName
   * @returns {string}
   */
  #getVariantSizeLabel(variant, sizeOptionName) {
    if (sizeOptionName) {
      const selected = this.#getVariantSelectedOptionValue(variant, sizeOptionName);
      if (selected) return selected;
    }

    const selectedOptions = Array.isArray(variant?.selectedOptions)
      ? variant.selectedOptions
      : [];
    const sizeOption = selectedOptions.find((option) =>
      String(option?.name || '').toLowerCase().includes('size')
    );
    if (sizeOption?.value) return String(sizeOption.value).trim();

    const title = String(variant?.title || '').trim();
    if (!title || title.toLowerCase() === 'default title') return '';

    const parts = title.split('/').map((part) => part.trim()).filter(Boolean);
    const fallback = parts.length > 1 ? parts[parts.length - 1] : '';
    return this.#looksLikeSizeLabel(fallback) ? fallback : '';
  }

  /**
   * @param {string} label
   * @returns {boolean}
   */
  #looksLikeSizeLabel(label) {
    const normalized = String(label || '').trim();
    if (!normalized) return false;
    return /^(one size|os|xxs|xs|s|m|l|xl|xxl|xxxl|\d{1,3}|[a-z]{1,3}\d{1,2}|\d{1,2}[a-z]{1,3})$/i.test(
      normalized
    );
  }

  /**
   * @param {any} product
   * @returns {string}
   */
  #formatRecentlyViewedDialogPrice(product) {
    const variants = Array.isArray(product.__variants) ? product.__variants : [];
    const prices = variants
      .map((variant) => Number.parseFloat(String(variant.price || '').replace(/,/g, '')))
      .filter((price) => !Number.isNaN(price));

    if (prices.length) {
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      if (min !== max) {
        return `From ${this.#formatRecentlyViewedPrice(min)}`;
      }
    }

    return this.#formatRecentlyViewedPrice(product.price || prices[0] || '');
  }

  /**
   * @param {string} productUrl
   * @returns {string}
   */
  #getProductHandleFromUrl(productUrl) {
    try {
      const url = new URL(productUrl, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch {
      const match = String(productUrl || '').match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    }
  }

  /**
   * Strip Shopify tracking query parameters (e.g. _pos, _psq, _ss, _v) from a product URL.
   * @param {string} productUrl
   * @returns {string}
   */
  #stripTrackingParams(productUrl) {
    try {
      const parsed = new URL(productUrl, window.location.origin);
      for (const key of [...parsed.searchParams.keys()]) {
        if (key.startsWith('_')) {
          parsed.searchParams.delete(key);
        }
      }
      return parsed.pathname + parsed.search;
    } catch {
      return productUrl;
    }
  }

  /**
   * Escape text before injecting into HTML string.
   * @param {string} value
   * @returns {string}
   */
  #escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  /**
   * Normalize price string for recently viewed cards.
   * @param {string | number | undefined} value
   * @returns {string}
   */
  #formatRecentlyViewedPrice(value) {
    if (value === undefined || value === null || value === '') return '';

    const valueAsString = String(value).trim();
    if (!valueAsString) return '';

    // Keep preformatted prices untouched.
    if (/[$€£¥]|\bAUD\b|\bUSD\b|\bEUR\b|\bGBP\b/i.test(valueAsString)) {
      return valueAsString;
    }

    const normalized = Number.parseFloat(valueAsString.replace(/,/g, ''));
    if (Number.isNaN(normalized)) {
      return valueAsString;
    }

    const cents = Math.round(normalized * 100);

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    const currencySymbol = window.currencySymbol || '$';
    const currencyCode = window?.currencyCode;

    if (currencyCode === 'JPY') {
      // For JPY, no decimal places unless needed, and add comma as thousand separator
      const jpyValue = Number.isInteger(normalized) ? normalized.toFixed(0) : normalized.toString();
      const parts = jpyValue.split('.');
      parts[0] = parts?.[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `${currencySymbol}${parts.join('.')}`;
    }
    return `${currencySymbol}${normalized.toFixed(2)}`;
  }

  /**
   * Render a loading skeleton while Recently Viewed products are being fetched.
   * @returns {string}
   */
  #buildRecentlyViewedLoadingMarkup() {
    const maxProducts = Number.parseInt(this.dataset.maxProducts || '4', 10) || 4;
    const skeletonItems = Array.from({ length: maxProducts })
      .map(
        () =>
          '<div class="product-recommendations__skeleton-item" aria-label="Loading recently viewed products"></div>'
      )
      .join('');

    return `<div class="resource-list resource-list--grid" data-has-recommendations="false">${skeletonItems}</div>`;
  }

  /**
   * Show centered loading indicator without replacing existing results content.
   * @param {HTMLElement} root
   */
  #showResultsLoading(root) {
    if (root.querySelector('[data-product-recommendations-loading]')) return;

    root.style.position = 'relative';
    root.setAttribute('aria-busy', 'true');

    const loading = document.createElement('div');
    loading.setAttribute('data-product-recommendations-loading', 'true');
    loading.setAttribute('aria-hidden', 'true');
    loading.innerHTML =
      '<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,0.72);z-index:4;"><span style="width:28px;height:28px;border:2px solid currentColor;border-top-color:transparent;border-radius:999px;display:inline-block;animation:pr-spin 0.8s linear infinite;"></span></div><style>@keyframes pr-spin{to{transform:rotate(360deg);}}</style>';

    root.appendChild(loading);
  }

  /**
   * Hide centered loading indicator.
   * @param {HTMLElement} root
   */
  #hideResultsLoading(root) {
    root.removeAttribute('aria-busy');
    root.querySelector('[data-product-recommendations-loading]')?.remove();
  }

  /**
   * Bind remove-from-wishlist behavior for wishlist tab cards.
   * @param {HTMLElement} root
   */
  #bindWishlistRemoveHandlers(root) {
    const buttons = root.querySelectorAll('[data-wishlist-remove="true"]');
    if (!buttons.length) return;

    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!window._swat) return;

        const { lid, epi, empi, du } = button.dataset;
        const wishlistKey = button.dataset.wishlistKey || '';
        const product = { epi, empi, du };
        const removedProductId = String(empi || '');

        const onSuccess = () => {
          const items = Array.from(root.querySelectorAll('.resource-list__item'));
          const matchedItems = wishlistKey
            ? items.filter((item) => item.dataset.wishlistKey === wishlistKey)
            : [button.closest('.resource-list__item')].filter(Boolean);

          matchedItems.forEach((item) => {
            const slide = item.closest('slideshow-slide');
            if (slide) {
              slide.remove();
            } else {
              item.remove();
            }
          });

          const hasAnyItem = root.querySelector('.resource-list__item');
          if (!hasAnyItem) {
            root.innerHTML =
              '<p class="product-recommendations__empty">No products in your wishlist.</p>';
            this.#wishlistMarkup = '';
            if (removedProductId) {
              this.#wishlistProductIds.delete(removedProductId);
              this.#syncRecentlyViewedWishlistIcons();
            }
            return;
          }

          if (removedProductId) {
            this.#wishlistProductIds.delete(removedProductId);
            this.#syncRecentlyViewedWishlistIcons();
          }

          this.#wishlistMarkup = root.innerHTML;
        };

        const onError = () => {
          button.disabled = false;
        };

        button.disabled = true;

        if (lid && typeof window._swat.deleteFromList === 'function') {
          window._swat.deleteFromList(lid, product, onSuccess, onError);
          return;
        }

        if (typeof window._swat.removeFromWishList === 'function') {
          window._swat.removeFromWishList(product, onSuccess, onError);
          return;
        }

        button.disabled = false;
      });
    });
  }

  /**
   * Render a loading skeleton while Wishlist products are being fetched.
   * @returns {string}
   */
  #buildWishlistLoadingMarkup() {
    const maxProducts = Number.parseInt(this.dataset.maxProducts || '4', 10) || 4;
    const skeletonItems = Array.from({ length: maxProducts })
      .map(
        () =>
          '<div class="product-recommendations__skeleton-item" aria-label="Loading wishlist products"></div>'
      )
      .join('');

    return `<div class="resource-list resource-list--grid" data-has-recommendations="false">${skeletonItems}</div>`;
  }

  /**
   * Bind Swym add/remove wishlist events for cross-tab sync.
   */
  #bindSwymWishlistEvents() {
    if (this.#swymEventsBound) return;

    const attach = (swat) => {
      if (!swat || !swat.evtLayer || !swat.JSEvents || this.#swymEventsBound) return;

      this.#swymEventsBound = true;

      swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, async (event) => {
        const productId = String(event?.detail?.d?.empi || event?.detail?.d?.product_id || '');
        if (!productId) return;

        this.#wishlistProductIds.add(productId);
        this.#wishlistMarkup = '';
        this.#syncRecentlyViewedWishlistIcons();
      });

      swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, async (event) => {
        const productId = String(event?.detail?.d?.empi || event?.detail?.d?.product_id || '');
        if (!productId) return;

        this.#wishlistProductIds.delete(productId);
        this.#wishlistMarkup = '';
        this.#syncRecentlyViewedWishlistIcons();
      });
    };

    if (window._swat) {
      attach(window._swat);
      return;
    }

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push((swat) => attach(swat));
  }

  /**
   * Update Recently Viewed wishlist icon active class from local state.
   */
  #syncRecentlyViewedWishlistIcons() {
    this.querySelectorAll('[data-recently-viewed-wishlist-trigger="true"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const productId = String(button.dataset.productId || '');
      const isActive = this.#wishlistProductIds.has(productId);
      button.classList.toggle('swym-added', isActive);
    });
  }

  /**
   * Bridge Searchspring sold-out size swatches to the shared Klaviyo BIS modal.
   */
  #bindSearchspringBisEvents() {
    if (this.#searchspringBisEventsBound) return;
    this.#searchspringBisEventsBound = true;

    this.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        const swatch = event.target.closest(
          '.product-thumbnail__swatch-item[data-variant-id]',
        );
        if (!(swatch instanceof HTMLElement) || !this.contains(swatch)) return;
        if (!this.#isSearchspringSoldOutSwatch(swatch)) return;

        const variantId = swatch.dataset.variantId || '';
        const result = swatch.closest('.ss__result, .swiper-slide');
        const productId =
          swatch
            .closest('[data-product-id]')
            ?.getAttribute('data-product-id') ||
          result?.querySelector('.swym-button[data-product-id]')?.getAttribute(
            'data-product-id',
          ) ||
          '';

        if (!productId || !variantId) return;

        event.preventDefault();
        event.stopPropagation();

        document.dispatchEvent(
          new CustomEvent('klaviyo-bis:open', {
            bubbles: true,
            detail: {
              productId,
              variantId,
              productHandle:
                this.#getProductHandleFromSearchspringResult(result),
            },
          }),
        );
      },
      { capture: true },
    );
  }

  /**
   * @param {HTMLElement} swatch
   * @returns {boolean}
   */
  #isSearchspringSoldOutSwatch(swatch) {
    if (
      swatch.classList.contains('sold-out') ||
      swatch.classList.contains('is-sold-out') ||
      swatch.classList.contains('unavailable') ||
      swatch.getAttribute('aria-disabled') === 'true' ||
      swatch.dataset.available === 'false'
    ) {
      return true;
    }

    return /sold\s*out|out\s*of\s*stock/i.test(swatch.textContent || '');
  }

  /**
   * @param {Element | null} result
   * @returns {string | undefined}
   */
  #getProductHandleFromSearchspringResult(result) {
    if (!(result instanceof Element)) return undefined;

    const href =
      result.querySelector('.product-thumbnail__title[href]')?.getAttribute(
        'href',
      ) ||
      result.querySelector('a[href*="/products/"]')?.getAttribute('href') ||
      '';

    if (!href) return undefined;

    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : undefined;
    } catch {
      const match = href.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : undefined;
    }
  }

}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

// ---------------------------------------------------------------------------
// addToCartGlobal — exposed for product cards inside the recommendations section
// ---------------------------------------------------------------------------

/** @returns {string[]} */
function getCartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  document.querySelectorAll('[data-section-id]').forEach((el) => {
    const id = el.getAttribute('data-section-id');
    if (id && !ids.includes(id)) ids.push(id);
  });
  return ids;
}

/**
 * @param {Record<string, string> | undefined} sections
 * @param {any} product
 */
function dispatchCartUpdate(sections, product) {
  const ev = /** @type {any} */ (new Event('cart:update', { bubbles: true }));
  ev.detail = {
    resource: product || {},
    sourceId: product?.variant_id ? String(product.variant_id) : '',
    data: {
      sections: sections || {},
      source: 'product-form-component',
      itemCount: product?.quantity || 1,
      productId: product?.product_id ? String(product.product_id) : undefined,
      variantId: product?.variant_id ? String(product.variant_id) : undefined,
    },
  };
  document.dispatchEvent(ev);
}

/** @type {any} */
const shopifyTheme = window.Shopify;
shopifyTheme.theme = shopifyTheme.theme || {};
shopifyTheme.theme.jsAjaxCart = shopifyTheme.theme.jsAjaxCart || {};

if (!shopifyTheme.theme.jsAjaxCart.addToCartGlobal) {
  /**
   * @param {any} formData
   * @param {Function} [callback]
   * @param {Function} [errorCallback]
   */
  shopifyTheme.theme.jsAjaxCart.addToCartGlobal = function addToCartGlobal(
    formData,
    callback,
    errorCallback,
  ) {
    const sectionIds = getCartSectionIds();
    const hasItemsArray =
      formData &&
      typeof formData === 'object' &&
      !(formData instanceof FormData) &&
      Array.isArray(formData.items);

    let fetchOptions;
    if (hasItemsArray) {
      const body = sectionIds.length
        ? { ...formData, sections: sectionIds.join(',') }
        : formData;
      fetchOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      };
    } else if (formData instanceof FormData) {
      if (sectionIds.length) formData.set('sections', sectionIds.join(','));
      fetchOptions = { method: 'POST', body: formData };
    } else {
      const params = new URLSearchParams(formData);
      if (sectionIds.length) params.set('sections', sectionIds.join(','));
      fetchOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params,
      };
    }

    fetch('/cart/add.js', fetchOptions)
      .then((res) => {
        if (!res.ok) return res.json().then((data) => Promise.reject(data));
        return res.json();
      })
      .then((product) => {
        dispatchCartUpdate(product.sections, product);

        if (shopifyTheme.theme.jsAjaxCart.showDrawer) {
          shopifyTheme.theme.jsAjaxCart.showDrawer();
        }

        try {
          const currentStoreCurrency = document.body.dataset.currencyIso;
          const productData = {
            content_id: `${product.product_id}`,
            content_type: 'product',
            content_name: `${product.product_title}`,
            price: product.price / 100.0,
            value: product.price / 100.0,
            currency: currentStoreCurrency,
          };
          const { ttq } = /** @type {any} */ (window);
          if (ttq) ttq.track('AddToCart', productData);
        } catch (e) {
          // TikTok pixel not available — ignore
        }

        if (typeof callback === 'function') callback();
      })
      .catch((err) => {
        const message =
          err?.description || err?.message || 'Error adding to cart';
        // eslint-disable-next-line no-console
        console.error(message);
        if (typeof errorCallback === 'function') errorCallback();
      });
  };
}
